/**
 * Клиент игрового сокета.
 *
 * Отвечает за: авторизацию первым сообщением, автопереподключение с
 * экспоненциальной паузой, повторный вход в комнату после обрыва и замер
 * задержки. Наружу отдаёт только события — состояние партии живёт в сторе.
 */
import type { ClientMessage, ServerMessage } from "@minigames/shared";

type Listener = (msg: ServerMessage) => void;

export type SocketStatus = "idle" | "connecting" | "online" | "offline";

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
const PING_INTERVAL_MS = 10_000;

export class GameSocket {
  #ws: WebSocket | null = null;
  #token: string | null = null;
  #listeners = new Set<Listener>();
  #statusListeners = new Set<(s: SocketStatus) => void>();
  #status: SocketStatus = "idle";

  /** Комнаты, в которые надо вернуться после переподключения. */
  #rooms = new Set<string>();
  #attempt = 0;
  #reconnectTimer: number | null = null;
  #pingTimer: number | null = null;
  #closedByUs = false;

  /** Оценка односторонней задержки, мс — показываем игроку. */
  latencyMs = 0;
  /** Смещение часов сервера относительно наших, мс. */
  clockSkewMs = 0;

  #pending = new Map<number, number>();
  #pingSeq = 0;

  get status(): SocketStatus {
    return this.#status;
  }

  connect(token: string): void {
    this.#token = token;
    this.#closedByUs = false;
    this.#open();
  }

  #open(): void {
    if (this.#ws && (this.#ws.readyState === WebSocket.OPEN || this.#ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.#setStatus("connecting");

    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      if (this.#token) this.send({ t: "auth", token: this.#token });
    };

    ws.onmessage = (e) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(e.data)) as ServerMessage;
      } catch {
        return;
      }

      if (msg.t === "auth:ok") {
        this.#setStatus("online");
        this.clockSkewMs = msg.serverTime - Date.now();
        // вернуться в комнаты, где были до обрыва
        for (const id of this.#rooms) this.send({ t: "join", matchId: id });
        this.#startPing();
      }

      if (msg.t === "pong") {
        const sentAt = this.#pending.get(msg.id);
        if (sentAt !== undefined) {
          this.#pending.delete(msg.id);
          this.latencyMs = Math.round((performance.now() - sentAt) / 2);
          this.clockSkewMs = msg.serverTime - Date.now();
        }
        return; // pong наружу не отдаём
      }

      for (const l of this.#listeners) l(msg);
    };

    ws.onclose = () => {
      this.#stopPing();
      this.#ws = null;
      if (this.#closedByUs) {
        this.#setStatus("idle");
        return;
      }
      this.#setStatus("offline");
      this.#scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose придёт следом — вся логика там
    };
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer !== null) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.#attempt, RECONNECT_MAX_MS);
    this.#attempt += 1;
    this.#reconnectTimer = window.setTimeout(() => {
      this.#reconnectTimer = null;
      this.#open();
    }, delay);
  }

  #startPing(): void {
    this.#stopPing();
    const tick = () => {
      const id = ++this.#pingSeq;
      this.#pending.set(id, performance.now());
      // не копим просроченные замеры
      if (this.#pending.size > 5) {
        const oldest = this.#pending.keys().next().value;
        if (oldest !== undefined) this.#pending.delete(oldest);
      }
      this.send({ t: "ping", id });
    };
    tick();
    this.#pingTimer = window.setInterval(tick, PING_INTERVAL_MS);
  }

  #stopPing(): void {
    if (this.#pingTimer !== null) {
      clearInterval(this.#pingTimer);
      this.#pingTimer = null;
    }
    this.#pending.clear();
  }

  #setStatus(s: SocketStatus): void {
    if (this.#status === s) return;
    this.#status = s;
    for (const l of this.#statusListeners) l(s);
  }

  send(msg: ClientMessage): void {
    if (this.#ws?.readyState === WebSocket.OPEN) {
      this.#ws.send(JSON.stringify(msg));
    }
  }

  joinMatch(matchId: string): void {
    this.#rooms.add(matchId);
    this.send({ t: "join", matchId });
  }

  leaveMatch(matchId: string): void {
    this.#rooms.delete(matchId);
    this.send({ t: "leave", matchId });
  }

  on(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  onStatus(listener: (s: SocketStatus) => void): () => void {
    this.#statusListeners.add(listener);
    listener(this.#status);
    return () => this.#statusListeners.delete(listener);
  }

  disconnect(): void {
    this.#closedByUs = true;
    this.#stopPing();
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    this.#ws?.close(1000, "bye");
    this.#ws = null;
  }
}

export const socket = new GameSocket();
